import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Job, JobStatus, User } from '@core/generated/gql-types';
import { useWorkerStore } from '@core/worker/useWorkerStore';

interface JobCancelInfoProps {
  job: Job;
  showWorkerLink?: boolean;
}

const JobCancelInfo = ({ job, showWorkerLink = true }: JobCancelInfoProps) => {
  const [cancelledOnWorker, setCancelledOnWorker] = useState<User | undefined>(
    undefined,
  );

  const { fetchWorkerById, getWorkerById } = useWorkerStore();
  const cancelledWhileFilledWorkerId = job.cancelledWhileFilledWorkerId;

  useEffect(() => {
    if (!cancelledWhileFilledWorkerId) return;

    const existingWorker = getWorkerById(cancelledWhileFilledWorkerId);
    if (existingWorker) {
      setCancelledOnWorker(existingWorker);
      return;
    }

    void fetchWorkerById(cancelledWhileFilledWorkerId).then((worker) => {
      setCancelledOnWorker(worker || undefined);
    });
  }, [cancelledWhileFilledWorkerId, fetchWorkerById, getWorkerById]);

  const cancelReason =
    job && 'cancelReason' in job
      ? (job as Job & { cancelReason?: string }).cancelReason
      : undefined;
  const cancelledAt =
    job && 'cancelledAt' in job
      ? (job as Job & { cancelledAt?: string }).cancelledAt
      : undefined;
  const cancelledAtFormatted =
    cancelledAt &&
    new Date(parseInt(cancelledAt, 10)).toLocaleDateString(undefined, {
      dateStyle: 'medium',
    });

  if (job?.itemStatus !== JobStatus.Cancelled) {
    return null;
  }

  return (
    <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
      <p className="text-sm font-semibold text-red-800">
        This job was cancelled
      </p>
      {cancelReason && (
        <p className="mt-1 text-sm text-gray-700">Reason: {cancelReason}</p>
      )}
      {cancelledAtFormatted && (
        <p className="mt-1 text-sm text-gray-700">
          Cancelled on: {cancelledAtFormatted}
        </p>
      )}
      {job.cancelledWhileFilledWorkerId && (
        <p className="mt-1 text-sm text-gray-700">
          Cancelled on worker:{' '}
          {showWorkerLink ? (
            <Link
              to={`/workers/${job.cancelledWhileFilledWorkerId}`}
              className="text-blue-600 hover:text-blue-700"
            >
              {cancelledOnWorker
                ? `${cancelledOnWorker?.firstName} ${cancelledOnWorker?.lastName}`
                : job.cancelledWhileFilledWorkerId}
            </Link>
          ) : (
            `${cancelledOnWorker?.firstName} ${cancelledOnWorker?.lastName}`
          )}
        </p>
      )}
    </div>
  );
};

export default JobCancelInfo;
